// Sessione di replay: un id opaco stabile per l'intera sessione-browser, più
// longevo del `trace_id` (che è per-pagina). Persistito in `sessionStorage` così
// da sopravvivere alle navigazioni full-page (Turbo Drive incluso); se lo storage
// non è disponibile (SSR, modalità privata, accesso negato) ricade su un id
// in-memory valido per la pagina corrente. L'id è casuale e NON contiene PII.

import { uuidV4NoDashes } from './uuid'

/// Chiave in sessionStorage e nome del cookie di correlazione lato server.
export const REPLAY_STORAGE_KEY = 'cyi_replay'

/// Sessione di replay corrente.
export interface ReplaySession {
  /// Id opaco condiviso da tutti i chunk e dagli errori della sessione.
  id: string
  /// Epoch ms di inizio sessione (t0 per la ricostruzione lato viewer).
  startedAt: number
}

/// Sottoinsieme di `Storage` usato (iniettabile nei test).
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

// Fallback quando lo storage non è disponibile: un'unica sessione in-memory per
// il contesto corrente (il browser è single-thread → una sessione per pagina).
let memorySession: ReplaySession | null = null

/// Ritorna la sessione di replay corrente creandola al primo accesso; idempotente
/// entro la stessa sessione-browser. [storage] iniettabile: `undefined` usa il
/// default (`sessionStorage` se disponibile), `null` forza il fallback in-memory.
export function ensureReplaySession(
  storage: StorageLike | null | undefined = defaultStorage(),
  now: () => number = () => Date.now(),
): ReplaySession {
  const store = storage === undefined ? defaultStorage() : storage
  if (store) {
    const existing = readStored(store)
    if (existing) return existing
    const created: ReplaySession = { id: uuidV4NoDashes(), startedAt: now() }
    writeStored(store, created)
    return created
  }
  if (memorySession) return memorySession
  memorySession = { id: uuidV4NoDashes(), startedAt: now() }
  return memorySession
}

/// Azzera il fallback in-memory. Solo per i test (isola le sessioni tra casi).
export function resetReplaySessionMemory(): void {
  memorySession = null
}

function readStored(storage: StorageLike): ReplaySession | null {
  let raw: string | null
  try {
    raw = storage.getItem(REPLAY_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<ReplaySession>
    if (typeof parsed.id === 'string' && typeof parsed.startedAt === 'number') {
      return { id: parsed.id, startedAt: parsed.startedAt }
    }
  } catch {
    // valore corrotto → rigenera
  }
  return null
}

function writeStored(storage: StorageLike, session: ReplaySession): void {
  try {
    storage.setItem(REPLAY_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // quota superata o accesso negato: la sessione resta valida per la pagina
  }
}

function defaultStorage(): StorageLike | null {
  try {
    if (typeof sessionStorage !== 'undefined') return sessionStorage
  } catch {
    // l'accesso a sessionStorage può sollevare (iframe cross-origin, storage off)
  }
  return null
}
